import styled from 'styled-components';
import DealershipObject from '../../../assets/images/pluvaAutoLanding/dealership_object.jpg';
import { TabContainer } from '../../../components/common/tab/TabContainer';
import {
  gyeonggiChungcheongRegions,
  gyeongsangRegions,
  jeollaRegions,
} from '../../../constants/autoLanding/dealershipRegions.constant';
import { PointBadge } from './PointBadge';

export interface RegionInfo {
  region: string;
  cityDealerList: {
    name: string;
    tel: string;
  }[];
}

const DealershipInfoContent = ({ regions }: { regions: RegionInfo[] }) => {
  return (
    <DealershipInfoContentContainer>
      {regions.map(({ region, cityDealerList: cityDealershipList }) => (
        <RegionCard key={region}>
          <RegionTitle>
            <RegionTitleText>{region}</RegionTitleText>
          </RegionTitle>
          <CityDealerList>
            {cityDealershipList.map(({ name, tel }) => (
              <CityDealerBox key={name}>
                <CityDealerName>{name}</CityDealerName>
                <CityDealerTel>{tel}</CityDealerTel>
              </CityDealerBox>
            ))}
          </CityDealerList>
        </RegionCard>
      ))}
    </DealershipInfoContentContainer>
  );
};

export const NationwideDealershipStatusSection = () => {
  return (
    <Container>
      <TitleSection>
        <PointBadge title={'전국 문의처'} />
        <Title>{'전국 각지의 플루바 오토\n대리점 현황!'}</Title>
        <Image src={DealershipObject} />
        <TitleHelperText>
          {'경기와 충청권, 전라권, 경상권까지\n전국 각지의 대리점에서\n구입문의를 할 수 있어요!'}
        </TitleHelperText>
      </TitleSection>
      <DealershipInfo>
        <TabContainer
          tabInfos={[
            {
              text: '경기 · 충청권',
              element: <DealershipInfoContent regions={gyeonggiChungcheongRegions} />,
            },
            {
              text: '전라권',
              element: <DealershipInfoContent regions={jeollaRegions} />,
            },
            {
              text: '경상권',
              element: <DealershipInfoContent regions={gyeongsangRegions} />,
            },
          ]}
        />
      </DealershipInfo>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 48px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Title = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  white-space: pre-line;
`;

const Image = styled.img`
  width: 180px;
`;

const TitleHelperText = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  white-space: pre-line;
`;

const DealershipInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const DealershipInfoContentContainer = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  @media screen and (max-width: 340px) {
    padding: 0;
  }
`;

const RegionCard = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;

  &:not(:first-child) {
    padding-top: 16px;
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  }
`;

const RegionTitle = styled.div`
  position: sticky;
  top: 100px;
  display: flex;
  width: 32px;
  height: 32px;
  padding: 16px;
  margin-bottom: 24px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-green, #34c77e);

  justify-content: center;
  align-items: center;
  white-space: nowrap;
  gap: 10px;
`;

const RegionTitleText = styled.h4`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 15.6px */
`;

const CityDealerList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
`;

const CityDealerBox = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  padding-bottom: 16px;

  &:not(:first-child) {
    padding-top: 16px;
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  }
`;

const CityDealerName = styled.p`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const CityDealerTel = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;
